/**
 * RegenControlBar — the pause / stop / resume strip for a single-section
 * regenerate. Rendered above the spine while a regen is in flight or paused.
 *
 * Separate from ``SpineControlBar`` (which controls the DRAFTING run) because a
 * regen runs on a completed run and never enters the reducer's mid-run state —
 * the shell stays in ``post-run``. Its lifecycle is owned by ``useRegenFlow``.
 * Stop and Pause both keep the section's previous draft (the server restores
 * it); Pause additionally lets the writer Resume, which restarts the regen with
 * the same guidance. Pure presentation.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import type { RegenActivity } from "../hooks/useRegenFlow";
import { fonts, inkColors, statusColors, surfaceColors } from "../tokens";

interface RegenControlBarProps {
  activity: RegenActivity | null;
  onPause: () => void;
  onStop: () => void;
  /** Finish the section now from what has been gathered. */
  onDraftNow?: () => void;
  onResume: () => void;
  onDiscard: () => void;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexShrink: 0,
    alignItems: "center",
    gap: "10px",
    background: surfaceColors.paper,
    borderBottom: `1px solid ${surfaceColors.line}`,
    padding: "8px 12px",
    fontFamily: fonts.body,
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: statusColors.hot,
    flexShrink: 0,
  },
  dotPaused: { background: statusColors.warn },
  label: {
    fontSize: "12px",
    fontWeight: 600,
    color: inkColors.ink,
    letterSpacing: "-0.1px",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  spacer: { flex: 1 },
  button: {
    background: "transparent",
    border: "none",
    borderRadius: "4px",
    color: inkColors.ink2,
    cursor: "pointer",
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    padding: "6px 10px",
    textTransform: "uppercase",
    flexShrink: 0,
    transition: "all 150ms",
    "&:hover": { color: statusColors.warn },
    "&:disabled": { opacity: 0.5, cursor: "default" },
  },
  stopButton: { "&:hover": { color: statusColors.danger } },
  draftNowButton: { "&:hover": { color: statusColors.cool } },
  resumeButton: { "&:hover": { color: statusColors.cool } },
});

const RegenControlBar: React.FC<RegenControlBarProps> = ({
  activity,
  onPause,
  onStop,
  onDraftNow,
  onResume,
  onDiscard,
}) => {
  const styles = useStyles();
  if (!activity) return null;

  const { phase } = activity;
  const isPaused = phase === "paused";
  const busy = phase === "pausing" || phase === "stopping" || phase === "finishing";
  const section = activity.sectionLabel ?? activity.sectionId;
  const resumeFailed = isPaused && Boolean(activity.error);
  const headline = resumeFailed
    ? `Couldn't resume ${section} — try Resume again`
    : isPaused
      ? `Regeneration paused · ${section}`
      : phase === "pausing"
        ? "Pausing…"
        : phase === "stopping"
          ? "Stopping…"
          : phase === "finishing"
            ? `Drafting ${section} from what was gathered…`
            : `Regenerating ${section}`;

  return (
    <output className={styles.root} aria-live="polite">
      <span className={mergeClasses(styles.dot, isPaused && styles.dotPaused)} aria-hidden />
      <span
        className={styles.label}
        title={
          resumeFailed
            ? `${activity.error} — previous draft kept.`
            : isPaused
              ? "Previous draft kept. Resume restarts this section."
              : undefined
        }
      >
        {headline}
      </span>
      <span className={styles.spacer} />
      {isPaused ? (
        <>
          <button type="button" className={mergeClasses(styles.button, styles.resumeButton)} onClick={onResume}>
            Resume
          </button>
          <button type="button" className={styles.button} onClick={onDiscard}>
            Discard
          </button>
        </>
      ) : (
        <>
          {onDraftNow && (
            <button
              type="button"
              className={mergeClasses(styles.button, styles.draftNowButton)}
              onClick={onDraftNow}
              disabled={busy}
              title="Stop retrieving and draft this section now from what has been gathered"
            >
              Draft now
            </button>
          )}
          <button type="button" className={styles.button} onClick={onPause} disabled={busy}>
            Pause
          </button>
          <button
            type="button"
            className={mergeClasses(styles.button, styles.stopButton)}
            onClick={onStop}
            disabled={busy}
          >
            Stop
          </button>
        </>
      )}
    </output>
  );
};

export default RegenControlBar;
